/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useRef, type ComponentType } from 'react';
import { EntryPointContainer, type EntryPoint, type EntryPointComponent } from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { RIGHT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { useActiveRightSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { RightPanel } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { renderGlobalComponents } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { sharedLayout } from '@jira/platform__layouts-jira-shared-layout/src/sharedLayout.tsx';
import { ReportSSRFailOnUse } from '@jira/ssr__rendering-guard/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlassian/jira-feature-gating';

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 * It's only added once the component is rendered client-side.
 */
const TEST_ID = 'page-layout.aside';

/* IMPORTANT:
 * When making any changes to the OLD component, please ensure that you also make the same changes to the
 * NEW component. The feature flag used is part of the Navigation Refresh work and we need to ensure that
 * we do not introduce tech debt for the roll-out by only updating the current component that is due to be
 * removed.
 *
 * If you have any concerns or questions please reach out to the #jira-navigation channel. Thanks!
 */

const routeComponents: ComponentType[] = [];
const SharedRightSidebars = () => <>{renderGlobalComponents(sharedLayout.rightSidebars)}</>;
const RightSidebarRouteComponents = () => {
	const route = useCurrentRoute();
	return <>{renderGlobalComponents(route.layout?.rightSidebars || routeComponents)}</>;
};

export const EmptySkeleton = (): null => null;

const RightSidebarThroughEntrypoint = () => {
	const rightSideBarEntryPointReference =
		usePageLayoutEntryPoint<
			EntryPoint<
				EntryPointComponent<{}, {}, {}, { fallback: () => JSX.Element | undefined }>,
				{ fallback: () => JSX.Element | undefined }
			>
		>(RIGHT_SIDEBAR);

	if (!rightSideBarEntryPointReference) return null;

	const RightSideBarFallback =
		rightSideBarEntryPointReference?.extraProps?.fallback || EmptySkeleton;

	return (
		<UFOSegment name="right-sidebar-through-entrypoint">
			<JSErrorBoundary
				fallback="unmount"
				id="right-sidebar-through-entrypoint"
				packageName="page-container"
			>
				<EntryPointReferencePlaceholder
					name="right-sidebar-through-entrypoint"
					fallback={
						<>
							<RightSideBarFallback />
							<ReportSSRFailOnUse
								pageId="navigation"
								fallbackId="RightSidebar"
								displayErrorInStaging
							/>
						</>
					}
					entryPointReference={rightSideBarEntryPointReference}
				>
					<EntryPointContainer
						entryPointReference={rightSideBarEntryPointReference}
						// oxlint-disable-next-line typescript/consistent-type-assertions
						props={{} as never}
					/>
				</EntryPointReferencePlaceholder>
			</JSErrorBoundary>
		</UFOSegment>
	);
};

export const RightSidebar = (): React.JSX.Element | null => {
	const lastPanelIdRef = useRef<unknown>();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const rightSidebarCurrentState = useActiveRightSidebarState();

	useEffect(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'rightSidebar change', {
			panelId: rightSidebarCurrentState?.panelId || lastPanelIdRef.current,
			isClosed: rightSidebarCurrentState?.panelId == null,
			isMinimised: !!rightSidebarCurrentState?.isMinimised,
			isCollapsed: !!rightSidebarCurrentState?.isCollapsed,
		});
		lastPanelIdRef.current = rightSidebarCurrentState?.panelId;
	}, [
		createAnalyticsEvent,
		rightSidebarCurrentState?.panelId,
		rightSidebarCurrentState?.isMinimised,
		rightSidebarCurrentState?.isCollapsed,
	]);

	if (rightSidebarCurrentState && !rightSidebarCurrentState.shouldRender) {
		return null;
	}

	const rightPanel = (
		<RightPanel
			width={
				rightSidebarCurrentState &&
				!rightSidebarCurrentState.isCollapsed &&
				!rightSidebarCurrentState.isMinimised
					? rightSidebarCurrentState.width
					: 0
			}
			testId={TEST_ID}
		>
			<>
				<JSErrorBoundary fallback="unmount" id="shared-right-sidebars" packageName="page-container">
					<SharedRightSidebars />
				</JSErrorBoundary>
				<JSErrorBoundary
					fallback="unmount"
					id="right-sidebar-route-components"
					packageName="page-container"
				>
					<RightSidebarRouteComponents />
				</JSErrorBoundary>
				<RightSidebarThroughEntrypoint />
			</>
		</RightPanel>
	);

	return (
		<UFOSegment name="right-sidebar">
			{fg('platform-dst-motion-uplift-panel') ? (
				<ExitingPersistence>{rightPanel}</ExitingPersistence>
			) : (
				rightPanel
			)}
		</UFOSegment>
	);
};
